import { motion } from "framer-motion";
import { Zap, Target, Crosshair } from "lucide-react";

const CARDS = [
  {
    icon: Zap,
    title: "Speed Without Compromise",
    desc:
      "Top‑tier candidates delivered within 48 hours. No volume hiring — only precision. With a 97.8% probation success rate, we back only talent that delivers.",
    kpi: "48h",
    kpiLabel: "Shortlist",
    accent: "green",
    color: "#D9F99D",
  },
  {
    icon: Target,
    title: "Elite Talent Access",
    desc:
      "Gain access to a deeply vetted network of high‑performing professionals — from Executive to C‑level leadership, ready to drive impact at scale.",
    kpi: "C‑Level",
    kpiLabel: "Network",
    accent: "purple",
    color: "#A78BFA",
  },
  {
    icon: Crosshair,
    title: "True Headhunting Approach",
    desc:
      "Every search is tailored to your business, your stage, and your goals. This is true headhunting — not database hiring. No recycled pipelines. No generic shortlists. Only high‑signal talent aligned with your vision.",
    kpi: "0",
    kpiLabel: "Recycled Pipes",
    accent: "blue",
    color: "#60A5FA",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90">
              Why Zevnox
            </div>
            <h2
              data-testid="why-us-heading"
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
            >
              The execution edge behind
              <br />
              <span className="zx-hi">category‑defining teams.</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-200">
            Built to operate at institutional tempo — where wrong hires cost
            millions, and the right ones compound for years.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`why-us-card-${i}`}
              data-accent={c.accent}
              className="zx-card group p-10 lg:p-12 min-h-[420px]"
            >
              {/* soft accent blob */}
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-60 transition-opacity duration-500 group-hover:opacity-100"
                style={{ background: `radial-gradient(circle, ${c.color}24, transparent 70%)`, filter: "blur(24px)" }}
              />
              <div className="relative flex items-start justify-between">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center border transition-all duration-500"
                  style={{
                    background: `${c.color}12`,
                    borderColor: `${c.color}35`,
                    color: c.color,
                    boxShadow: `0 0 24px ${c.color}22`,
                  }}
                >
                  <c.icon size={26} />
                </div>
                <div className="text-right">
                  <div className="font-mono text-3xl text-white">{c.kpi}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {c.kpiLabel}
                  </div>
                </div>
              </div>
              <h3 className="relative mt-8 font-display text-2xl sm:text-3xl text-white leading-tight">
                {c.title}
              </h3>
              <p className="relative mt-5 text-neutral-200 leading-relaxed text-[15px]">
                {c.desc}
              </p>
              <div
                className="relative mt-8 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em]"
                style={{ color: c.color }}
              >
                <span className="h-1 w-1 rounded-full zx-pulse" style={{ background: c.color }} />
                0{i + 1} / 03
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
